import { supabase } from "@/lib/supabase";
import { queryClient } from "@/lib/queryClient";
import { fetchUserProfile } from "@/hooks/useUser";
import {
  ALL_PROFILES_QUERY_KEY,
  USERS_STALE_TIME,
} from "@/lib/users-cache";
import { profilesListSchema, type Profile } from "@/types/users";

export interface CreateUserDTO {
  email: string;
  password?: string;
  firstName: string;
  lastName: string;
  role: string;
  team: string;
}

class UserService {
  private static instance: UserService;

  private constructor() {}

  public static getInstance(): UserService {
    if (!UserService.instance) {
      UserService.instance = new UserService();
    }
    return UserService.instance;
  }

  async getCurrentUserRole() {
    try {
      const { profile } = await queryClient.fetchQuery({
        queryKey: ["authUser"],
        queryFn: fetchUserProfile,
        staleTime: 1000 * 60 * 5,
      });
      
      return profile?.role || null;
    } catch (error) {
      return null;
    }
  }

  async fetchAllProfiles(): Promise<Profile[]> {
    const { data, error } = await supabase
      .from("profiles")
      .select("*")
      .order("created_at", { ascending: false });

    if (error) throw new Error(error.message);

    const parsed = profilesListSchema.safeParse(data);
    if (!parsed.success) {
      console.error("Invalid profiles response:", parsed.error);
      return [];
    }

    return parsed.data;
  }

  async getAll(): Promise<Profile[]> {
    return queryClient.fetchQuery({
      queryKey: ALL_PROFILES_QUERY_KEY,
      queryFn: () => this.fetchAllProfiles(),
      staleTime: USERS_STALE_TIME,
    });
  }

  async create(user: CreateUserDTO) {
    const { data, error } = await supabase.functions.invoke("create-user", {
      body: user,
    });

    if (error) throw error;

    return data;
  }

  async delete(userId: string) {
    const { error } = await supabase.functions.invoke("delete-user", {
      body: { user_id: userId },
    });

    if (error) throw error;

    return true;
  }
}

export const userService = UserService.getInstance();